import { useState, useEffect } from 'react'
import { AiOutlineSearch, AiOutlineCheckCircle, AiOutlineDisconnect, AiOutlineSync } from 'react-icons/ai'

import {
  ContainerHome,
  MainContent,
  ConnectionCard,
  Title,
  Subtitle,
  LoadingMessage,
  ContainerQRCode,
  InputContainer,
  InputCNPJ,
  ConnectButton,
  Loading,
  LogoHome,
  Qrcode,
  StatusHome,
  SuccessMessage,
  ErrorMessage,
  DisconnectButton,
  ReconnectButton,
  ButtonGroup
} from "../../styles"

import socket from "../../socket"

export default function Home() {
  const [qrsource, setQrSource] = useState('')
  const [status, setStatus] = useState('')
  const [cnpj, setCnpj] = useState('')
  const [companyData, setCompanyData] = useState<any | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [error, setError] = useState('')
  const [connectedInfo, setConnectedInfo] = useState<{ number?: string, name?: string } | null>(null)

  useEffect(() => {
    socket.on('qrcode', (qrbase64: string) => {
      console.log('QR Code recebido')
      setQrSource(`${qrbase64}`)
      setIsConnecting(true)
    })
    
    socket.on('status', (sessao: string) => {
      console.log('Status:', sessao)
      setStatus(`${sessao}`)
      
      // Verifica se a conexão foi bem-sucedida
      if (sessao.includes('authenticated') || sessao.includes('ready') || sessao.includes('Conectado') || sessao.includes('sucesso')) {
        setIsConnected(true)
        setIsConnecting(false)
        setQrSource('')
      }

      // Verifica se desconectou
      if (sessao.includes('Desconectado') || sessao.includes('nova conexão')) {
        setIsConnected(false)
        setIsConnecting(false)
        setIsDisconnecting(false)
        setConnectedInfo(null)
      }
    })

    // Adicionar listener para o evento 'authenticated' também
    socket.on('authenticated', () => {
      console.log('Authenticated recebido')
      setIsConnected(true)
      setIsConnecting(false)
      setQrSource('')
      setStatus('Conectado com sucesso!')
    })

    return () => {
      socket.off('qrcode')
      socket.off('status')
      socket.off('authenticated')
    }
  }, [])

  const formatCNPJ = (value: string) => {
    // Remove tudo que não é dígito
    const cnpjNumbers = value.replace(/\D/g, '')
    
    // Aplica a máscara do CNPJ
    if (cnpjNumbers.length <= 14) {
      return cnpjNumbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    }
    return cnpjNumbers.substring(0, 14).replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }

  const searchCompany = async () => {
    const cnpjNumbers = cnpj.replace(/\D/g, '')
    if (cnpjNumbers.length !== 14 && cnpjNumbers.length !== 11) {
      setError('CNPJ ou CPF deve conter 14 ou 11 dígitos')
      return
    }
    setIsLoading(true)
    setError('')
    try {
      const response = await fetch('https://apisiacweb.herokuapp.com/touch/cnpj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cnpj: cnpjNumbers })
      })
      
      if (!response.ok) {
        throw new Error('Empresa não encontrada')
      }
      const data = await response.json()
      setCompanyData(data)
      console.log('Empresa encontrada:', data)
      setIsLoading(false)
      
      // Conectar automaticamente após encontrar a empresa
      connectWhatsApp(data)
    } catch (err) {
      setError('Erro ao buscar empresa. Verifique o CNPJ e tente novamente.')
      setCompanyData(null)
      setIsLoading(false)
    }
  }

  const connectWhatsApp = (data?: any) => {
    const company = data || companyData
    if (!company) {
      setError('Dados da empresa não encontrados')
      return
    }

    // Evitar múltiplas conexões simultâneas
    if (isConnecting) {
      console.log('Já está conectando, ignorando nova tentativa')
      return
    }

    setIsConnecting(true)
    setError('')
    socket.emit('msg', { msg: company })
  }

  const disconnectWhatsApp = () => {
    if (!companyData) {
      setError('Dados da empresa não encontrados')
      return
    }

    setIsDisconnecting(true)
    setError('')
    
    const sessionName = companyData
    socket.emit('disconnect_session', { session_name: sessionName })
  }

  const reconnectWhatsApp = () => {
    if (!companyData) {
      setError('Dados da empresa não encontrados')
      return
    }

    setIsConnecting(true)
    setError('')
    setQrSource('')
    
    const sessionName = companyData
    socket.emit('reconnect_session', { session_name: sessionName })
  }

  const resetConnection = () => {
    setIsConnected(false)
    setIsConnecting(false)
    setQrSource('')
    setCompanyData(null)
    setConnectedInfo(null)
    setStatus('')
    setCnpj('')
    setError('')
  }

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNPJ(e.target.value)
    setCnpj(formatted)
    
    // Limpa dados anteriores quando o CNPJ é alterado
    if (companyData) {
      setCompanyData(null)
      setError('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && !isConnecting && !isConnected && !companyData) {
      searchCompany()
    }
  }

  return (
    <ContainerHome>
      <MainContent>
        <LogoHome src="./logo.png" alt="Logo" />
        <ConnectionCard>
          <Title>Conexão WhatsApp</Title>
          <Subtitle>
            {qrsource && isConnecting
              ? 'Leia o QR Code com o celular que possui o WhatsApp. Abra o app, acesse Menu > Dispositivos conectados > Conectar um dispositivo e aponte para o QR Code.'
              : 'Digite o CNPJ da empresa para configurar a conexão automática do WhatsApp'}
          </Subtitle>

          {!isConnected && !companyData && (
            <InputContainer>
              <InputCNPJ
                placeholder="00.000.000/0000-00"
                value={cnpj}
                onChange={handleCNPJChange}
                onKeyPress={handleKeyPress}
                disabled={isLoading || isConnecting}
                maxLength={18}
              />
              <ConnectButton
                onClick={searchCompany}
                disabled={isLoading || isConnecting || !cnpj.replace(/\D/g, '').length}
              >
                {isLoading ? (
                  <Loading />
                ) : (
                  <>
                    <AiOutlineSearch />
                    Buscar
                  </>
                )}
              </ConnectButton>
            </InputContainer>
          )}

          {error && (
            <ErrorMessage>{error}</ErrorMessage>
          )}

          {companyData && !isConnected && !qrsource && (
            <LoadingMessage>
              <Loading />
              Gerando QrCode...
            </LoadingMessage>
          )}

          {isConnected && (
            <>
              <SuccessMessage>
                <h3>
                  <AiOutlineCheckCircle style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Conexão Configurada!
                </h3>
                <p>O WhatsApp foi conectado com sucesso. Agora você pode utilizar as mensagens automáticas.</p>
                {connectedInfo && (
                  <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                    {connectedInfo.name && `Nome: ${connectedInfo.name}`}
                    {connectedInfo.number && ` | Número: ${connectedInfo.number}`}
                  </p>
                )}
              </SuccessMessage>
              
              <ButtonGroup>
                <DisconnectButton 
                  onClick={disconnectWhatsApp} 
                  disabled={isDisconnecting}
                  title="Desconectar para trocar de dispositivo"
                >
                  {isDisconnecting ? (
                    <Loading style={{ width: '20px', height: '20px', borderWidth: '2px' }} />
                  ) : (
                    <>
                      <AiOutlineDisconnect />
                      Trocar Dispositivo
                    </>
                  )}
                </DisconnectButton>
                
                <ConnectButton 
                  onClick={resetConnection}
                >
                  Nova Conexão
                </ConnectButton>
              </ButtonGroup>
            </>
          )}

          {qrsource && isConnecting && (
            <ContainerQRCode>
              <Qrcode 
                src={qrsource} 
                alt="QR Code WhatsApp"
                onError={(e) => {
                  e.currentTarget.src = 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d1/Image_not_available.png/800px-Image_not_available.png'
                }} 
              />
            </ContainerQRCode>
          )}

          {/* Botão de reconectar quando falhar */}
          {companyData && !isConnected && !isConnecting && !qrsource && status.includes('Erro') && (
            <ButtonGroup>
              <ReconnectButton onClick={reconnectWhatsApp}>
                <AiOutlineSync />
                Tentar Novamente
              </ReconnectButton>
            </ButtonGroup>
          )}

          {status && !isConnected && (
            <StatusHome>
              {status}
            </StatusHome>
          )}
        </ConnectionCard>
      </MainContent>
    </ContainerHome>
  )
}